/**
 * Premium tutor voice — ElevenLabs first (sellable quality), open-source fallback later.
 *
 * Priority (default mode = elevenlabs):
 *   1. API /speech/synthesize — speech-rewriter LoRA + ElevenLabs on server
 *   2. Browser ElevenLabs direct (VITE_ELEVEN_KEY)
 *   3. Sesame CSM self-hosted (when VOICE_TTS=sesame or ElevenLabs unavailable)
 *   4. Browser speechSynthesis (dev fallback only)
 */

import { resolveElevenVoiceId, voiceTtsMode } from "./voices";

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

const ELEVEN_KEY = import.meta.env.VITE_ELEVEN_KEY as string | undefined;

export type VoiceEngine =
  | "sesame-whisper"
  | "elevenlabs-api"
  | "elevenlabs-browser"
  | "browser"
  | "none";

let _currentAudio: HTMLAudioElement | null = null;
let _audioCtx: AudioContext | null = null;
let _analyser: AnalyserNode | null = null;
let _lastEngine: VoiceEngine = "none";
let _preferredVoiceKey = "rachel";
let _audioUnlocked = false;
/** Incremented on every new speak/stop — in-flight TTS aborts when stale. */
let _speakGeneration = 0;
let _fetchAbort: AbortController | null = null;

export function isSpeechActive(): boolean {
  return _currentAudio !== null && !_currentAudio.paused;
}

/** Call from a user click/tap before async work so later audio.play() is allowed. */
export async function unlockAudio(): Promise<void> {
  if (_audioUnlocked) return;
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    await ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0.0001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.02);
    await ctx.close();
    _audioUnlocked = true;
  } catch {
    /* best-effort */
  }
}

export function setPreferredVoiceKey(key: string) {
  _preferredVoiceKey = key;
}

export function getLastVoiceEngine(): VoiceEngine {
  return _lastEngine;
}

export function getAmplitude(): number {
  if (!_analyser) return 0;
  const data = new Uint8Array(_analyser.frequencyBinCount);
  _analyser.getByteFrequencyData(data);
  const speechBins = data.slice(0, 32);
  const avg = speechBins.reduce((a, b) => a + b, 0) / (speechBins.length * 255);
  return Math.min(1, avg * 3.5);
}

export function cleanForSpeech(text: string): string {
  return text
    .replace(/#{1,6}\s+/g, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/`{1,3}[^`]*`{1,3}/g, "")
    .replace(/\\\(|\\\)|\\\[|\\\]/g, "")
    .replace(/\$\$[\s\S]*?\$\$/g, "")
    .replace(/\$[^$\n]+\$/g, "")
    .replace(/Step \d+:/gi, "")
    .replace(/\d+\.\s+/g, "")
    .replace(/[-*•]\s+/g, "")
    .replace(/_{1,2}(.+?)_{1,2}/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\n{2,}/g, ". ")
    .replace(/\n/g, " ")
    .replace(/:\s*\n/g, ". ")
    .replace(/\s{2,}/g, " ")
    .replace(/\.\s*\./g, ".")
    .trim();
}

export function stopSpeech() {
  _speakGeneration += 1;
  _fetchAbort?.abort();
  _fetchAbort = null;
  if (_currentAudio) {
    _currentAudio.pause();
    _currentAudio.onended = null;
    _currentAudio.onerror = null;
    _currentAudio.src = "";
    _currentAudio = null;
  }
  _analyser = null;
  _audioCtx?.close().catch(() => {});
  _audioCtx = null;
  window.speechSynthesis?.cancel();
}

async function playAudioBuffer(
  buffer: ArrayBuffer,
  mime: string,
  generation: number,
  onStart?: () => void,
  onEnd?: () => void,
): Promise<void> {
  if (generation !== _speakGeneration) return;
  if (!buffer.byteLength) throw new Error("empty audio buffer");

  await unlockAudio();
  if (generation !== _speakGeneration) return;

  const blob = new Blob([buffer], { type: mime });
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  audio.setAttribute("playsinline", "true");
  audio.volume = 1;
  _currentAudio = audio;

  try {
    const ctx = new AudioContext();
    await ctx.resume();
    if (generation !== _speakGeneration) {
      URL.revokeObjectURL(url);
      return;
    }
    _audioCtx = ctx;
    const src = ctx.createMediaElementSource(audio);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    src.connect(analyser);
    analyser.connect(ctx.destination);
    _analyser = analyser;
  } catch {
    _analyser = null;
  }

  await new Promise<void>((resolve, reject) => {
    if (generation !== _speakGeneration) {
      URL.revokeObjectURL(url);
      resolve();
      return;
    }
    audio.onplay = () => {
      if (generation === _speakGeneration) onStart?.();
    };
    audio.onended = () => {
      if (generation === _speakGeneration) onEnd?.();
      _analyser = null;
      _audioCtx?.close().catch(() => {});
      _audioCtx = null;
      URL.revokeObjectURL(url);
      if (_currentAudio === audio) _currentAudio = null;
      resolve();
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("audio playback failed"));
    };
    audio
      .play()
      .catch(() => unlockAudio().then(() => audio.play()))
      .catch(reject);
  });
}

async function speakViaApi(
  text: string,
  voiceId: string,
  generation: number,
  onStart?: () => void,
  onEnd?: () => void,
): Promise<boolean> {
  try {
    _fetchAbort?.abort();
    const ctrl = new AbortController();
    _fetchAbort = ctrl;
    const res = await fetch(`${API_BASE}/speech/synthesize`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, rewrite: false, voice_id: voiceId }),
      signal: ctrl.signal,
    });
    if (generation !== _speakGeneration) return false;
    if (!res.ok) return false;
    const buf = await res.arrayBuffer();
    if (generation !== _speakGeneration) return false;
    if (!buf.byteLength) return false;
    _lastEngine = "elevenlabs-api";
    await playAudioBuffer(buf, "audio/mpeg", generation, onStart, onEnd);
    return generation === _speakGeneration;
  } catch {
    return false;
  }
}

async function speakElevenLabsBrowser(
  text: string,
  voiceId: string,
  generation: number,
  onStart?: () => void,
  onEnd?: () => void,
): Promise<boolean> {
  if (!ELEVEN_KEY) return false;
  try {
    const res = await fetch(
      `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}/stream`,
      {
        method: "POST",
        headers: {
          "xi-api-key": ELEVEN_KEY,
          "Content-Type": "application/json",
          Accept: "audio/mpeg",
        },
        body: JSON.stringify({
          text,
          model_id: "eleven_multilingual_v2",
          optimize_streaming_latency: 2,
          voice_settings: {
            stability: 0.42,
            similarity_boost: 0.88,
            style: 0.32,
            use_speaker_boost: true,
          },
        }),
      },
    );
    if (generation !== _speakGeneration) return false;
    if (!res.ok) return false;
    const buf = await res.arrayBuffer();
    if (generation !== _speakGeneration) return false;
    _lastEngine = "elevenlabs-browser";
    await playAudioBuffer(buf, "audio/mpeg", generation, onStart, onEnd);
    return generation === _speakGeneration;
  } catch {
    return false;
  }
}

async function speakViaVoiceAgent(
  text: string,
  onStart?: () => void,
  onEnd?: () => void,
): Promise<boolean> {
  try {
    const { speakSesame, fetchVoiceAgentStatus } = await import("./voice-agent");
    const st = await fetchVoiceAgentStatus();
    if (!st.ready) return false;
    _lastEngine = "sesame-whisper";
    await speakSesame(text, { rewrite: !import.meta.env.VITE_VOICE_URL, onStart, onEnd });
    return true;
  } catch {
    return false;
  }
}

function speakBrowser(
  text: string,
  generation: number,
  onStart?: () => void,
  onEnd?: () => void,
): Promise<void> {
  return new Promise((resolve) => {
    if (!window.speechSynthesis) {
      onEnd?.();
      resolve();
      return;
    }
    if (generation !== _speakGeneration) {
      resolve();
      return;
    }
    _lastEngine = "browser";
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 0.92;
    utt.pitch = 1.05;
    const voices = window.speechSynthesis.getVoices();
    const preferred =
      voices.find(
        (v) =>
          v.lang === "en-US" &&
          (v.name.includes("Google") ||
            v.name.includes("Samantha") ||
            v.name.includes("Karen")),
      ) || voices.find((v) => v.lang.startsWith("en"));
    if (preferred) utt.voice = preferred;
    utt.onstart = () => {
      if (generation === _speakGeneration) onStart?.();
    };
    utt.onend = () => {
      if (generation === _speakGeneration) onEnd?.();
      resolve();
    };
    utt.onerror = () => resolve();
    window.speechSynthesis.speak(utt);
  });
}

/**
 * Speak with the best available voice. ElevenLabs is the default premium path.
 */
export async function speak(
  text: string,
  onStart?: () => void,
  onEnd?: () => void,
  preferredVoiceKey?: string,
): Promise<void> {
  const cleaned = cleanForSpeech(text);
  const clean = cleaned.length >= 8 ? cleaned : text.replace(/\s+/g, " ").trim().slice(0, 800);
  if (!clean) {
    onEnd?.();
    return;
  }

  stopSpeech();
  const generation = _speakGeneration;
  await unlockAudio();

  const voiceId = resolveElevenVoiceId(preferredVoiceKey ?? _preferredVoiceKey);
  const mode = voiceTtsMode();
  const trySesame = mode === "sesame" || mode === "auto";

  if (mode !== "sesame") {
    if (await speakViaApi(clean, voiceId, generation, onStart, onEnd)) return;
    if (await speakElevenLabsBrowser(clean, voiceId, generation, onStart, onEnd)) return;
  }

  if (generation !== _speakGeneration) return;

  if (trySesame && (await speakViaVoiceAgent(clean, onStart, onEnd))) return;

  if (mode === "auto") {
    if (await speakViaApi(clean, voiceId, generation, onStart, onEnd)) return;
    if (await speakElevenLabsBrowser(clean, voiceId, generation, onStart, onEnd)) return;
  }

  if (generation === _speakGeneration) {
    await speakBrowser(clean, generation, onStart, onEnd);
  }
}

export async function fetchVoiceStatus(): Promise<{
  provider: string;
  configured: boolean;
  engine: VoiceEngine;
  label: string;
}> {
  if (ELEVEN_KEY) {
    return {
      provider: "elevenlabs",
      configured: true,
      engine: "elevenlabs-browser",
      label: "ElevenLabs Premium",
    };
  }
  try {
    const res = await fetch(`${API_BASE}/speech/status`);
    if (res.ok) {
      const data = (await res.json()) as { provider: string; configured: boolean };
      if (data.provider === "elevenlabs" && data.configured) {
        return {
          provider: "elevenlabs",
          configured: true,
          engine: "elevenlabs-api",
          label: "ElevenLabs Premium",
        };
      }
      if (data.configured) {
        return {
          provider: data.provider,
          configured: true,
          engine: "elevenlabs-api",
          label: data.provider,
        };
      }
    }
  } catch {
    /* fall through */
  }
  return {
    provider: "browser",
    configured: false,
    engine: "browser",
    label: "Browser voice (add ELEVENLABS_API_KEY)",
  };
}
